import React, { useState, useEffect, useCallback } from 'react'

import { TrashIcon } from 'images'
import { useTranslation } from 'react-i18next'
import { SecondaryButton } from 'components/Shared/Button'
import { downloadLibraryImage, fetchLibraryGroup } from 'api/library'
import MediaCard from 'components/Shared/MediaCard/MediaCard'
import MediaListContainer from 'components/Recommendations/Campaign/Media/elements/MediaListV2/MediaListContainer'
import { mapLibraryItemToMediaCard } from 'components/Library/mapLibraryItem'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import TableMessage from 'components/Shared/TableMessage'
import { localizeDateObj } from 'utils/helpers'

const PreviewLibraryModal = ({
  file,
  onDeleteClick,
  deletedImageId,
  onDeletedImageHandled,
}) => {
  const [groupImages, setGroupImages] = useState([])
  const [isFetchingGroupImages, setIsFetchingGroupImages] = useState(false)
  const [errorFetchingGroupImages, setErrorFetchingGroupImages] = useState(false)
  const [currentImage, setCurrentImage] = useState(file)
  const [isDownloading, setIsDownloading] = useState(false)

  const isVideo = currentImage?.type?.startsWith('video')
  const { t } = useTranslation()
  const formattedCreatedAt = currentImage?.created_at
    ? localizeDateObj(currentImage.created_at, null, 'DD.MM.YYYY')
    : '-'

  const handleDownload = useCallback(async () => {
    if (!currentImage?.id || !currentImage?.group_id || isDownloading) return

    const fallbackFilename = currentImage.name || currentImage.filename || 'download'

    setIsDownloading(true)
    try {
      const response = await downloadLibraryImage({
        id: currentImage.id,
        group_id: currentImage.group_id,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error)
      }

      const contentType = response.headers?.['content-type']?.split(';')[0]
      const blob = contentType ? new Blob([response.data], { type: contentType }) : response.data
      const contentDisposition = response.headers?.['content-disposition']
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : fallbackFilename

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download library image:', error)
      window.open(currentImage.url, '_blank', 'noopener,noreferrer')
    } finally {
      setIsDownloading(false)
    }
  }, [currentImage, isDownloading])

  useEffect(() => {
    async function fetchGroupImages() {
      setIsFetchingGroupImages(true)
      setErrorFetchingGroupImages(false)

      const res = await fetchLibraryGroup({ image_id: file.id, group_id: file.group_id })

      if (res.success) {
        const items = Array.isArray(res.data) ? res.data : res.data?.items ?? []
        setGroupImages(items.map(mapLibraryItemToMediaCard))
      } else {
        setErrorFetchingGroupImages(true)
      }
      setIsFetchingGroupImages(false)
    }
    fetchGroupImages()
  }, [file])

  useEffect(() => {
    if (!deletedImageId) {
      return
    }

    setGroupImages((prev) => prev.filter((image) => image.id !== deletedImageId))
    setCurrentImage(file)
    onDeletedImageHandled?.()
  }, [deletedImageId, file, onDeletedImageHandled])

  return (
    <div className="library-preview-modal__content">
      <div className="media-preview">
        {isVideo ? (
          <video controls src={currentImage.url} title={currentImage.name || currentImage.filename} />
        ) : (
          <img src={currentImage.url} alt={currentImage.name || currentImage.filename} />
        )}
      </div>

      <div className="media-info-container">
        <dl className="media-info">
          <dt className="media-info__label">{t('Library.previewModal.fileName')}</dt>
          <dd className="media-info__value">{currentImage.name || currentImage.filename}</dd>

          <dt className="media-info__label">{t('Library.previewModal.date')}</dt>
          <dd className="media-info__value">{formattedCreatedAt}</dd>

          <dt className="media-info__label">{t('Library.previewModal.dimensions')}</dt>
          <dd className="media-info__value">{currentImage.width} x {currentImage.height}</dd>

          <dt className="media-info__label">{t('Library.previewModal.format')}</dt>
          <dd className="media-info__value">{currentImage.format || '-'}</dd>
        </dl>

        <div className="media-info-actions">
          <button
            type="button"
            className="delete-button"
            onClick={() => onDeleteClick?.(currentImage)}
          >
            <TrashIcon width={16} height={16} />
          </button>

          <SecondaryButton
            color="navy"
            fullWidth={false}
            loading={isDownloading}
            disabled={!currentImage?.id || !currentImage?.group_id}
            onClick={handleDownload}
          >
            {t('Library.previewModal.download')}
          </SecondaryButton>
        </div>
      </div>

      <div className="media-group-container">
        <div className='d-flex gap-2 align-items-center'>
          <p className="media-group-container__title">{t('Library.previewModal.imageFamily')}</p>

          {!isFetchingGroupImages && <p className="media-group-container__count">{groupImages.length}</p>}
        </div>

        {isFetchingGroupImages && <div className="media-group-container__loading">
          <LoadingSpinner text={t('common.loading')} />
        </div>}

        {errorFetchingGroupImages && (
          <TableMessage variant="error" description={t('Library.previewModal.error')} className="mt-2" />
        )}

        {!isFetchingGroupImages && !errorFetchingGroupImages && groupImages.length === 0 && (
          <TableMessage variant="empty" description={t('Library.previewModal.empty')} className="mt-2" />
        )}

        {!isFetchingGroupImages && !errorFetchingGroupImages && groupImages.length > 0 && (
          <MediaListContainer>
            {groupImages.map((image) => (
              <MediaCard
                key={image.id}
                item={image}
                isGroupHighlighted={image.id === currentImage?.id}
                onClick={() => setCurrentImage(image)}
              />
            ))}
          </MediaListContainer>
        )}
      </div>
    </div>
  )
}

export default PreviewLibraryModal